import { Step, MultiQuestion } from '@de/types/types';
import { ConditionType, LogicType, StepType } from '@de/types/enums';

/**
 * Walk the default branching path forward from `currentStepId` and count
 * how many non-RESULT steps remain until a terminal step is reached.
 *
 * "Default path" means we always follow the `DEFAULT` condition branch.
 * We stop when we hit a terminal step, a RESULT step, a missing step,
 * or a cycle.
 *
 * MULTI_QUESTION child steps are excluded from the count so that a
 * multi-question group is only counted once (as the parent step).
 */
export function computeRemainingDefaultSteps(
  steps: Step[],
  currentStepId: string,
): number {
  const stepMap = new Map<string, Step>();
  const childQuestionIds = new Set<string>();
  for (const step of steps) {
    if (step.id) {
      stepMap.set(step.id, step);
    }
    if (step.type === StepType.MULTI_QUESTION && 'stepQuestions' in step) {
      const mq = step as Step & MultiQuestion;
      mq.stepQuestions?.forEach((q) => q.id && childQuestionIds.add(q.id));
    }
  }

  let remaining = 0;
  const visited = new Set<string>();
  let cursor = currentStepId;

  while (true) {
    const step = stepMap.get(cursor);
    if (!step) break;

    // Mark visited to detect cycles
    if (visited.has(cursor)) break;
    visited.add(cursor);

    // Find the DEFAULT branch
    const defaultBranch = step.branchingLogic?.find(
      (bl) => bl.condition.type === ConditionType.DEFAULT,
    );
    if (!defaultBranch) break;

    // RESULT branches don't count as navigable steps
    if (defaultBranch.type === LogicType.RESULT) break;

    const nextStep = stepMap.get(defaultBranch.nextId);
    if (!nextStep) break;

    // Don't count RESULT-type steps toward remaining
    if (nextStep.type === StepType.RESULT) break;

    // Don't count MQ child questions — the parent step already represents them
    if (childQuestionIds.has(defaultBranch.nextId)) {
      cursor = defaultBranch.nextId;
      if (nextStep.isTerminal) break;
      continue;
    }

    remaining++;
    cursor = defaultBranch.nextId;

    // If we've reached a terminal step, stop walking further
    if (nextStep.isTerminal) break;
  }

  return remaining;
}
